import { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Plus,
    Minus,
    RefreshCw,
    MapPin,
    X,
    ZoomIn,
    Share2,
    Check,
    Maximize2,
    Minimize2,
    List,
} from "lucide-react";
import { api, resolveMediaUrl } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useLang } from "@/i18n/LangContext";

const MIN_ZOOM = 1;
const MAX_ZOOM = 12;
const DEFAULT_CATEGORY = {
    slug: "general",
    label: "Lokalizacja",
    color: "#facc15",
    icon: "",
};

export default function InteractiveMap({
    mapImageUrl,
    pois = [],
    initialPoiKey = null,
    onSelectionChange,
    siteUrl = "",
}) {
    const [selected, setSelected] = useState(null);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [categories, setCategories] = useState([]);
    const [shareCopied, setShareCopied] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const { t, lang } = useLang();
    // null = "all visible" (default). When user toggles, becomes a Set of
    // active slugs. We use Set rather than boolean per-slug so reconciling
    // with admin-edited categories (added/removed/renamed) is automatic:
    // unknown slugs simply aren't in the set => hidden when filter active.
    const [activeSlugs, setActiveSlugs] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get("/poi-categories");
                setCategories(res.data || []);
            } catch (e) {
                // categories optional; fall back to default
            }
        })();
    }, []);

    const categoryMap = categories.reduce((acc, c) => {
        acc[c.slug] = c;
        return acc;
    }, {});
    const getCategory = (slug) => categoryMap[slug] || DEFAULT_CATEGORY;

    // Slugs actually used by POIs (covers POIs that reference deleted
    // categories - they fall back to DEFAULT_CATEGORY's slug 'general').
    const poiSlugs = Array.from(
        new Set(pois.map((p) => (categoryMap[p.category] ? p.category : DEFAULT_CATEGORY.slug)))
    );

    // Categories to render in the filter UI: all admin-defined categories
    // plus a synthetic "general" fallback if POIs use it but it's missing.
    const filterCategories = categories.length
        ? categories
        : [DEFAULT_CATEGORY];

    const isActive = (slug) => activeSlugs === null || activeSlugs.has(slug);

    const toggleSlug = (slug) => {
        setActiveSlugs((prev) => {
            if (prev === null) {
                // First click: start from "all on", turn this one OFF.
                const all = new Set(filterCategories.map((c) => c.slug).concat(poiSlugs));
                all.delete(slug);
                return all;
            }
            const next = new Set(prev);
            if (next.has(slug)) next.delete(slug);
            else next.add(slug);
            return next;
        });
    };

    const showOnly = (slug) => {
        // Solo: show only this category. If already solo, restore all.
        setActiveSlugs((prev) => {
            if (prev && prev.size === 1 && prev.has(slug)) {
                return null;
            }
            return new Set([slug]);
        });
    };

    const resetFilter = () => setActiveSlugs(null);

    const visiblePois = pois.filter((p) => {
        const slug = categoryMap[p.category] ? p.category : DEFAULT_CATEGORY.slug;
        return isActive(slug);
    });

    // Group visible POIs by category for the side list. Preserve admin
    // category order; unknown/general slugs go last.
    const groupedPois = (() => {
        const groups = new Map();
        for (const c of filterCategories) groups.set(c.slug, []);
        for (const p of visiblePois) {
            const slug = categoryMap[p.category] ? p.category : DEFAULT_CATEGORY.slug;
            if (!groups.has(slug)) groups.set(slug, []);
            groups.get(slug).push(p);
        }
        // Return only non-empty groups
        return Array.from(groups.entries())
            .map(([slug, items]) => ({ slug, items }))
            .filter((g) => g.items.length > 0);
    })();

    // Clear the selected POI if it got filtered out
    useEffect(() => {
        if (selected && !visiblePois.find((p) => p.id === selected.id)) {
            setSelected(null);
            setLightboxOpen(false);
        }
    }, [activeSlugs, pois, selected, visiblePois]);

    // Deep-link: auto-select POI from initialPoiKey (URL like /poi/<slug-or-id>).
    // Match against both slug and legacy UUID id so both formats keep working.
    useEffect(() => {
        if (!initialPoiKey || !pois.length) return;
        const target =
            pois.find((p) => p.slug === initialPoiKey) ||
            pois.find((p) => p.id === initialPoiKey);
        if (target && (!selected || selected.id !== target.id)) {
            setSelected(target);
        }
    }, [initialPoiKey, pois.length, selected]);

    // Sync selection up to parent + reflect in URL so the link can be shared.
    useEffect(() => {
        if (typeof onSelectionChange === "function") {
            onSelectionChange(selected?.id || null);
        }
        if (typeof window === "undefined") return;
        try {
            const url = new URL(window.location.href);
            if (selected) {
                // Prefer canonical /poi/<slug> form; preserve hash so #mapa still works
                const key = selected.slug || selected.id;
                const newPath = `/poi/${key}`;
                if (url.pathname !== newPath) {
                    window.history.replaceState({}, "", `${newPath}${url.search}${url.hash || "#mapa"}`);
                }
            } else if (url.pathname.startsWith("/poi/")) {
                // Closing detail returns to root URL
                window.history.replaceState({}, "", `/${url.search}${url.hash || ""}`);
            }
        } catch (e) {
            /* ignore */
        }
    }, [selected?.id, selected?.slug, onSelectionChange]);

    const buildShareUrl = (poi) => {
        if (!poi) return "";
        const key = poi.slug || poi.id;
        if (!key) return "";
        const base =
            (siteUrl || "").replace(/\/$/, "") ||
            (typeof window !== "undefined" ? window.location.origin : "");
        return `${base}/poi/${key}`;
    };

    const sharePoi = async () => {
        if (!selected) return;
        const url = buildShareUrl(selected);
        const shareData = {
            title: selected.title,
            text: selected.description || selected.title,
            url,
        };
        // Track the share attempt regardless of channel (native/clipboard) so
        // the admin dashboard can see which POIs get spread the most. Fired
        // once per click - even native-cancelled shares count as "intent",
        // but if user cancels the browser share sheet we still count it
        // because a) it's tricky to know for sure, b) intent is the useful
        // signal, and c) the fallback clipboard path also fires.
        try {
            trackEvent("poi_share", { label: selected.id || selected.slug || "" });
        } catch (e) { /* ignore analytics failures */ }
        try {
            if (navigator.share) {
                await navigator.share(shareData);
                return;
            }
        } catch (e) {
            /* user cancelled \u2014 fall through to copy */
        }
        try {
            await navigator.clipboard.writeText(url);
            setShareCopied(true);
            setTimeout(() => setShareCopied(false), 1800);
        } catch (e) {
            /* ignore */
        }
    };

    const mapSrc =
        resolveMediaUrl(mapImageUrl) ||
        "https://static.prod-images.emergentagent.com/jobs/359e1a58-6c2d-46ea-8fb3-26c1f78309e7/images/3731fdb140d58211a26e9163309a47c5bf2fa9c3c2956d688b47d564407e2299.png";

    // ===== Zoom & Pan state (same engine as admin) =====
    const containerRef = useRef(null);
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
    const [imgNatural, setImgNatural] = useState({ w: 0, h: 0 });
    const dragStart = useRef({ x: 0, y: 0, panX: 0, panY: 0, moved: false });

    // Track container dimensions so POI markers can be positioned in a
    // non-scaled overlay layer (keeps them crisp and constant-size on zoom).
    useEffect(() => {
        const c = containerRef.current;
        if (!c) return;
        const update = () =>
            setContainerSize({ w: c.clientWidth, h: c.clientHeight });
        update();
        const ro = new ResizeObserver(update);
        ro.observe(c);
        return () => ro.disconnect();
    }, []);

    // Compute the actual displayed image rectangle inside the container for an
    // object-contain image. Markers are positioned relative to THIS rect (not
    // the raw container) so they sit on the same map point regardless of the
    // container's aspect ratio - keeping admin and public maps consistent.
    const getImageRect = useCallback(() => {
        const { w: cw, h: ch } = containerSize;
        const { w: iw, h: ih } = imgNatural;
        if (!cw || !ch || !iw || !ih) {
            return { ox: 0, oy: 0, dw: cw, dh: ch };
        }
        const scale = Math.min(cw / iw, ch / ih);
        const dw = iw * scale;
        const dh = ih * scale;
        return { ox: (cw - dw) / 2, oy: (ch - dh) / 2, dw, dh };
    }, [containerSize, imgNatural]);

    const clampPan = useCallback((px, py, z) => {
        const c = containerRef.current;
        if (!c) return { x: px, y: py };
        const w = c.clientWidth;
        const h = c.clientHeight;
        const minX = w * (1 - z);
        const minY = h * (1 - z);
        return {
            x: Math.min(0, Math.max(minX, px)),
            y: Math.min(0, Math.max(minY, py)),
        };
    }, []);

    const setZoomAt = useCallback(
        (newZoom, anchorX, anchorY) => {
            const c = containerRef.current;
            if (!c) return;
            const clampedZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, newZoom));
            if (clampedZoom === zoom) return;
            const ax = anchorX ?? c.clientWidth / 2;
            const ay = anchorY ?? c.clientHeight / 2;
            const ratio = clampedZoom / zoom;
            const newPanX = ax - (ax - pan.x) * ratio;
            const newPanY = ay - (ay - pan.y) * ratio;
            setPan(clampPan(newPanX, newPanY, clampedZoom));
            setZoom(clampedZoom);
        },
        [zoom, pan, clampPan]
    );

    const zoomIn = () => setZoomAt(zoom * 1.4);
    const zoomOut = () => setZoomAt(zoom / 1.4);
    const resetView = () => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    };

    useEffect(() => {
        const c = containerRef.current;
        if (!c) return;
        const onWheel = (e) => {
            e.preventDefault();
            const rect = c.getBoundingClientRect();
            const mx = e.clientX - rect.left;
            const my = e.clientY - rect.top;
            const factor = Math.exp(-e.deltaY * 0.0015);
            setZoomAt(zoom * factor, mx, my);
        };
        c.addEventListener("wheel", onWheel, { passive: false });
        return () => c.removeEventListener("wheel", onWheel);
    }, [zoom, setZoomAt]);

    useEffect(() => {
        const onUp = () => setDragging(false);
        window.addEventListener("mouseup", onUp);
        return () => window.removeEventListener("mouseup", onUp);
    }, []);

    const onMapMouseDown = (e) => {
        if (e.button !== 0) return;
        setDragging(true);
        dragStart.current = {
            x: e.clientX,
            y: e.clientY,
            panX: pan.x,
            panY: pan.y,
            moved: false,
        };
    };

    const onMapMouseMove = (e) => {
        if (!dragging) return;
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        if (!dragStart.current.moved && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
            dragStart.current.moved = true;
        }
        if (dragStart.current.moved) {
            setPan(
                clampPan(
                    dragStart.current.panX + dx,
                    dragStart.current.panY + dy,
                    zoom
                )
            );
        }
    };

    const onMapMouseUp = () => setDragging(false);

    // Touch support: simple single-finger pan + pinch zoom
    const touchState = useRef({
        startDist: 0,
        startZoom: 1,
        startPan: { x: 0, y: 0 },
        startTouch: { x: 0, y: 0 },
        anchor: { x: 0, y: 0 },
        mode: null, // 'pan' | 'pinch'
    });

    const onTouchStart = (e) => {
        const c = containerRef.current;
        if (!c) return;
        const rect = c.getBoundingClientRect();
        if (e.touches.length === 2) {
            const [t1, t2] = e.touches;
            const dx = t2.clientX - t1.clientX;
            const dy = t2.clientY - t1.clientY;
            touchState.current = {
                startDist: Math.hypot(dx, dy),
                startZoom: zoom,
                startPan: { ...pan },
                anchor: {
                    x: (t1.clientX + t2.clientX) / 2 - rect.left,
                    y: (t1.clientY + t2.clientY) / 2 - rect.top,
                },
                startTouch: { x: 0, y: 0 },
                mode: "pinch",
            };
        } else if (e.touches.length === 1) {
            touchState.current = {
                ...touchState.current,
                startPan: { ...pan },
                startTouch: { x: e.touches[0].clientX, y: e.touches[0].clientY },
                mode: "pan",
            };
        }
    };

    const onTouchMove = (e) => {
        const c = containerRef.current;
        if (!c || !touchState.current.mode) return;
        if (touchState.current.mode === "pinch" && e.touches.length === 2) {
            e.preventDefault();
            const [t1, t2] = e.touches;
            const dx = t2.clientX - t1.clientX;
            const dy = t2.clientY - t1.clientY;
            const dist = Math.hypot(dx, dy);
            const ratio = dist / touchState.current.startDist;
            const newZoom = Math.min(
                MAX_ZOOM,
                Math.max(MIN_ZOOM, touchState.current.startZoom * ratio)
            );
            const ax = touchState.current.anchor.x;
            const ay = touchState.current.anchor.y;
            const z0 = touchState.current.startZoom;
            const px0 = touchState.current.startPan.x;
            const py0 = touchState.current.startPan.y;
            const r = newZoom / z0;
            const newPan = clampPan(ax - (ax - px0) * r, ay - (ay - py0) * r, newZoom);
            setZoom(newZoom);
            setPan(newPan);
        } else if (touchState.current.mode === "pan" && e.touches.length === 1) {
            e.preventDefault();
            const t = e.touches[0];
            const dx = t.clientX - touchState.current.startTouch.x;
            const dy = t.clientY - touchState.current.startTouch.y;
            setPan(
                clampPan(
                    touchState.current.startPan.x + dx,
                    touchState.current.startPan.y + dy,
                    zoom
                )
            );
        }
    };

    const onTouchEnd = () => {
        touchState.current.mode = null;
    };

    // Fullscreen handling: ESC to exit, body scroll lock while active.
    useEffect(() => {
        if (!fullscreen) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e) => {
            if (e.key === "Escape") {
                if (lightboxOpen) {
                    setLightboxOpen(false);
                } else {
                    setFullscreen(false);
                    setDrawerOpen(false);
                }
            }
        };
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener("keydown", onKey);
        };
    }, [fullscreen, lightboxOpen]);

    const toggleFullscreen = () => {
        setFullscreen((v) => {
            const next = !v;
            if (!next) setDrawerOpen(false);
            return next;
        });
    };

    const handleMarkerClick = (e, p) => {
        if (dragStart.current.moved) {
            dragStart.current.moved = false;
            return;
        }
        e.stopPropagation();
        setLightboxOpen(false);
        setSelected(p);
        trackEvent("poi_click", { label: p?.id || "" });
    };

    return (
        <section
            id="mapa"
            data-testid="map-section"
            className="relative py-24 md:py-32 bg-[color:var(--bg-surface)]"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                    <div>
                        <div className="label-eyebrow mb-4">{t("03 - Interaktywna mapa")}</div>
                        <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl tracking-tighter text-white">
                            {lang === "en" ? t("Punkty na trasie") : <>Punkty<br />na trasie</>}
                        </h2>
                    </div>
                    <p className="text-base text-[color:var(--text-secondary)] max-w-md">
                        {t("Przeciągaj, przybliżaj scrollem i klikaj świecące znaczniki, aby poznać kluczowe lokalizacje moda.")}
                    </p>
                </div>

                <div className={`grid lg:grid-cols-12 gap-6 ${fullscreen ? "lg:grid-cols-1" : ""}`}>
                    <div className={`${fullscreen ? "fixed inset-0 z-[80] bg-[color:var(--bg-surface)] !rounded-none !border-0" : "lg:col-span-9 relative"} panel overflow-hidden`}>
                        {/* Zoom controls */}
                        <div className="absolute top-4 right-4 z-20 flex flex-col gap-2">
                            <button
                                data-testid="map-zoom-in"
                                onClick={zoomIn}
                                disabled={zoom >= MAX_ZOOM - 0.001}
                                className="panel-yellow !rounded-md w-10 h-10 flex items-center justify-center text-[color:var(--primary)] hover:bg-[color:var(--primary)] hover:text-[color:var(--bg-base)] disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Powiększ"
                            >
                                <Plus size={16} />
                            </button>
                            <button
                                data-testid="map-zoom-out"
                                onClick={zoomOut}
                                disabled={zoom <= MIN_ZOOM + 0.001}
                                className="panel-yellow !rounded-md w-10 h-10 flex items-center justify-center text-[color:var(--primary)] hover:bg-[color:var(--primary)] hover:text-[color:var(--bg-base)] disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Pomniejsz"
                            >
                                <Minus size={16} />
                            </button>
                            <button
                                data-testid="map-reset"
                                onClick={resetView}
                                disabled={zoom === 1 && pan.x === 0 && pan.y === 0}
                                className="panel-yellow !rounded-md w-10 h-10 flex items-center justify-center text-[color:var(--primary)] hover:bg-[color:var(--primary)] hover:text-[color:var(--bg-base)] disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Resetuj"
                            >
                                <RefreshCw size={14} />
                            </button>
                            <button
                                data-testid="map-fullscreen"
                                onClick={toggleFullscreen}
                                className="panel-yellow !rounded-md w-10 h-10 flex items-center justify-center text-[color:var(--primary)] hover:bg-[color:var(--primary)] hover:text-[color:var(--bg-base)]"
                                aria-label={fullscreen ? "Wyjdź z trybu pełnoekranowego" : "Pełny ekran"}
                                title={fullscreen ? t("Wyjdź z pełnego ekranu (Esc)") : t("Pełny ekran")}
                            >
                                {fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                            </button>
                            {fullscreen && (
                                <button
                                    data-testid="map-fullscreen-list-toggle"
                                    onClick={() => setDrawerOpen((v) => !v)}
                                    className={`panel-yellow !rounded-md w-10 h-10 flex items-center justify-center hover:bg-[color:var(--primary)] hover:text-[color:var(--bg-base)] ${
                                        drawerOpen
                                            ? "!bg-[color:var(--primary)] !text-[color:var(--bg-base)]"
                                            : "text-[color:var(--primary)]"
                                    }`}
                                    aria-label={drawerOpen ? "Zamknij listę punktów" : "Pokaż listę punktów"}
                                    title={drawerOpen ? t("Zamknij listę") : t("Lista punktów")}
                                >
                                    <List size={14} />
                                </button>
                            )}
                        </div>
                        {/* Zoom level badge */}
                        <div
                            data-testid="map-zoom-level"
                            className="absolute top-4 left-4 z-20 panel-yellow !rounded-md px-2.5 py-1 text-[10px] uppercase tracking-wider text-[color:var(--text-secondary)]"
                        >
                            {Math.round(zoom * 100)}%
                        </div>

                        <div
                            ref={containerRef}
                            data-testid="public-map-container"
                            onMouseDown={onMapMouseDown}
                            onMouseMove={onMapMouseMove}
                            onMouseUp={onMapMouseUp}
                            onMouseLeave={onMapMouseUp}
                            onTouchStart={onTouchStart}
                            onTouchMove={onTouchMove}
                            onTouchEnd={onTouchEnd}
                            className={`relative w-full overflow-hidden bg-[color:var(--bg-surface)] select-none touch-none ${
                                dragging
                                    ? "cursor-grabbing"
                                    : zoom > 1
                                    ? "cursor-grab"
                                    : "cursor-default"
                            }`}
                            style={{ height: fullscreen ? "100vh" : "min(70vh, 640px)" }}
                        >
                            <div
                                className="absolute inset-0"
                                style={{
                                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                                    transformOrigin: "0 0",
                                    willChange: "transform",
                                }}
                            >
                                <img
                                    src={mapSrc}
                                    alt="Mapa Rudy Śląskiej w ETS2"
                                    draggable={false}
                                    loading="lazy"
                                    decoding="async"
                                    onLoad={(e) =>
                                        setImgNatural({
                                            w: e.target.naturalWidth,
                                            h: e.target.naturalHeight,
                                        })
                                    }
                                    className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
                                />
                            </div>

                            {/* POI markers - rendered in a separate, NON-scaled
                                overlay so they stay crisp and keep a constant
                                pixel size at any zoom level. Positions are
                                computed manually from pan + zoom, relative to
                                the actual displayed image rect. */}
                            <div className="absolute inset-0 pointer-events-none">
                                {visiblePois.map((p) => {
                                    const cat = getCategory(p.category);
                                    const sz =
                                        (p.size || 14) *
                                        Math.min(3.5, Math.sqrt(zoom));
                                    const isSelected = selected?.id === p.id;
                                    const { ox, oy, dw, dh } = getImageRect();
                                    const left =
                                        pan.x +
                                        zoom * (ox + (p.x / 100) * dw);
                                    const top =
                                        pan.y +
                                        zoom * (oy + (p.y / 100) * dh);
                                    return (
                                        <button
                                            key={p.id}
                                            data-testid={`map-marker-${p.id}`}
                                            onClick={(e) => handleMarkerClick(e, p)}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            className="absolute rounded-full border-2 border-white flex items-center justify-center shadow-lg transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/60 pointer-events-auto"
                                            style={{
                                                left: `${left}px`,
                                                top: `${top}px`,
                                                background: cat.color,
                                                color: "#000",
                                                width: `${sz}px`,
                                                height: `${sz}px`,
                                                fontSize: `${Math.max(8, sz * 0.6)}px`,
                                                transform: `translate(-50%, -50%) ${
                                                    isSelected ? "scale(1.25)" : ""
                                                }`,
                                                lineHeight: 1,
                                                boxShadow: isSelected
                                                    ? `0 0 0 4px ${cat.color}66`
                                                    : "0 2px 6px rgba(0,0,0,0.4)",
                                            }}
                                            aria-label={p.title}
                                            title={p.title}
                                        >
                                            {cat.icon}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        {!fullscreen && (
                            <div className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-muted)] border-t border-white/5">
                                {t("Scroll = zoom · Przeciągnij = przesuń · Klik na marker = szczegóły")}
                            </div>
                        )}
                    </div>

                    <aside
                        className={
                            fullscreen
                                ? `fixed top-0 right-0 bottom-0 z-[90] w-[88vw] max-w-md panel-yellow p-6 overflow-y-auto !rounded-none border-l border-white/10 shadow-2xl transition-transform duration-300 ${
                                      drawerOpen ? "translate-x-0" : "translate-x-full"
                                  }`
                                : "lg:col-span-3 panel-yellow p-6 max-h-[640px] overflow-y-auto"
                        }
                        data-testid="map-poi-list"
                    >
                        <div className="flex items-center justify-between mb-3 gap-2">
                            <div className="label-eyebrow !mb-0">
                                {t("Punkty")} ({visiblePois.length}
                                {visiblePois.length !== pois.length ? `/${pois.length}` : ""})
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                {activeSlugs !== null && (
                                    <button
                                        data-testid="map-filter-reset"
                                        onClick={resetFilter}
                                        className="text-[10px] uppercase tracking-wider text-[color:var(--primary)] hover:underline shrink-0"
                                    >
                                        {t("Pokaż wszystkie")}
                                    </button>
                                )}
                                {fullscreen && (
                                    <button
                                        type="button"
                                        data-testid="map-drawer-close"
                                        onClick={() => setDrawerOpen(false)}
                                        className="flex items-center justify-center w-8 h-8 rounded-full border border-white/10 text-[color:var(--text-secondary)] hover:text-[color:var(--primary)] hover:border-[color:var(--primary)]/40 transition-colors"
                                        aria-label="Zamknij listę punktów"
                                        title={t("Zamknij listę")}
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                        </div>
                        {groupedPois.length === 0 && (
                            <div className="text-sm text-[color:var(--text-secondary)]">
                                {pois.length === 0
                                    ? t("Brak punktów. Admin może dodać je w panelu.")
                                    : t("Brak punktów dla wybranych kategorii.")}
                            </div>
                        )}
                        <div className="flex flex-col gap-5">
                            {groupedPois.map((group) => {
                                const cat = getCategory(group.slug);
                                return (
                                    <div
                                        key={group.slug}
                                        data-testid={`map-poi-group-${group.slug}`}
                                    >
                                        <div className="flex items-center gap-2 mb-2">
                                            <span
                                                className="rounded-full border-2 border-white flex items-center justify-center text-[10px] shrink-0"
                                                style={{
                                                    background: cat.color,
                                                    color: "#000",
                                                    width: 18,
                                                    height: 18,
                                                }}
                                            >
                                                {cat.icon || (
                                                    <MapPin size={9} strokeWidth={2.5} />
                                                )}
                                            </span>
                                            <span className="text-[11px] uppercase tracking-[0.18em] text-white font-semibold truncate">
                                                {t(cat.label)}
                                            </span>
                                            <span className="text-[10px] text-[color:var(--text-muted)] ml-auto shrink-0">
                                                {group.items.length}
                                            </span>
                                        </div>
                                        <ul className="flex flex-col gap-2">
                                            {group.items.map((p) => (
                                                <li key={p.id}>
                                                    <button
                                                        data-testid={`map-list-item-${p.id}`}
                                                        onClick={() => {
                                                            setLightboxOpen(false);
                                                            setSelected(p);
                                                            // In fullscreen, auto-close drawer on small viewports
                                                            // so the user can see the map + detail panel.
                                                            if (fullscreen && typeof window !== "undefined" && window.innerWidth < 1024) {
                                                                setDrawerOpen(false);
                                                            }
                                                            trackEvent("poi_click", { label: p?.id || "" });
                                                        }}
                                                        className={`w-full text-left p-3 rounded-md border transition-colors ${
                                                            selected?.id === p.id
                                                                ? "border-[color:var(--primary)] bg-[color:var(--primary)]/5"
                                                                : "border-white/5 hover:border-white/20"
                                                        }`}
                                                    >
                                                        <div className="flex items-start gap-2.5">
                                                            <span
                                                                className="rounded-full border-2 border-white flex items-center justify-center text-[10px] shrink-0 mt-0.5"
                                                                style={{
                                                                    background: cat.color,
                                                                    color: "#000",
                                                                    width: 20,
                                                                    height: 20,
                                                                }}
                                                            >
                                                                {cat.icon || (
                                                                    <MapPin
                                                                        size={10}
                                                                        strokeWidth={2.5}
                                                                    />
                                                                )}
                                                            </span>
                                                            <div className="min-w-0">
                                                                <div className="text-sm text-white font-medium truncate">
                                                                    {t(p.title)}
                                                                </div>
                                                                <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-muted)] mt-1">
                                                                    {t(cat.label)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                );
                            })}
                        </div>
                    </aside>
                </div>

                {/* Legend / filter */}
                {filterCategories.length > 0 && !fullscreen && (
                    <div
                        data-testid="map-legend"
                        className="mt-6 panel-yellow p-5"
                    >
                        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                            <div className="label-eyebrow !mb-0">
                                Legenda · filtr kategorii
                            </div>
                            <div className="flex items-center gap-3">
                                {activeSlugs !== null && (
                                    <button
                                        data-testid="map-filter-all"
                                        onClick={resetFilter}
                                        className="text-[10px] uppercase tracking-wider text-[color:var(--primary)] hover:underline"
                                    >
                                        Pokaż wszystkie
                                    </button>
                                )}
                                <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-muted)]">
                                    Klik = włącz/wyłącz · Dwuklik = tylko ta
                                </span>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {filterCategories.map((c) => {
                                const active = isActive(c.slug);
                                const count = pois.filter((p) => {
                                    const s = categoryMap[p.category]
                                        ? p.category
                                        : DEFAULT_CATEGORY.slug;
                                    return s === c.slug;
                                }).length;
                                return (
                                    <button
                                        type="button"
                                        key={c.id || c.slug}
                                        data-testid={`map-legend-${c.slug}`}
                                        onClick={() => toggleSlug(c.slug)}
                                        onDoubleClick={() => showOnly(c.slug)}
                                        aria-pressed={active}
                                        title={
                                            active
                                                ? t("Kliknij aby ukryć · Dwuklik = tylko ta kategoria")
                                                : t("Kliknij aby pokazać")
                                        }
                                        className={`flex items-center gap-2.5 px-3 py-1.5 rounded-full border transition-all ${
                                            active
                                                ? "border-white/30 bg-white/5 hover:border-[color:var(--primary)]"
                                                : "border-white/10 bg-transparent opacity-40 hover:opacity-70"
                                        }`}
                                    >
                                        <span
                                            className="rounded-full border-2 border-white flex items-center justify-center text-[10px] shrink-0"
                                            style={{
                                                background: c.color,
                                                color: "#000",
                                                width: 20,
                                                height: 20,
                                            }}
                                        >
                                            {c.icon}
                                        </span>
                                        <span className="text-sm text-white">{t(c.label)}</span>
                                        <span className="text-[10px] text-[color:var(--text-muted)] ml-1">
                                            ({count})
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {selected && (
                    <div
                        data-testid="map-poi-detail"
                        className={
                            fullscreen
                                ? "fixed left-4 right-4 bottom-4 sm:left-6 sm:right-auto sm:bottom-6 sm:max-w-xl z-[85] panel-yellow p-6 relative shadow-2xl max-h-[60vh] overflow-y-auto"
                                : "mt-6 panel-yellow p-6 relative"
                        }
                    >
                        <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
                            <button
                                type="button"
                                data-testid="map-poi-share"
                                onClick={sharePoi}
                                title={shareCopied ? t("Skopiowano!") : t("Udostępnij ten punkt")}
                                className="flex items-center gap-1.5 px-3 h-9 rounded-full bg-[color:var(--bg-base)]/80 backdrop-blur-sm border border-white/10 text-[color:var(--text-secondary)] hover:text-[color:var(--primary)] hover:border-[color:var(--primary)]/40 transition-colors text-xs uppercase tracking-wider"
                            >
                                {shareCopied ? <Check size={14} /> : <Share2 size={14} />}
                                <span className="hidden sm:inline">
                                    {shareCopied ? t("Skopiowano") : t("Udostępnij")}
                                </span>
                            </button>
                            <button
                                className="flex items-center justify-center w-9 h-9 rounded-full bg-[color:var(--bg-base)]/80 backdrop-blur-sm border border-white/10 text-[color:var(--text-secondary)] hover:text-[color:var(--primary)] hover:border-[color:var(--primary)]/40 transition-colors"
                                onClick={() => {
                                    setLightboxOpen(false);
                                    setSelected(null);
                                }}
                                aria-label="Zamknij szczegóły"
                                data-testid="map-poi-close"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="grid md:grid-cols-12 gap-6 items-start">
                            <div className="md:col-span-8 pr-12 md:pr-0">
                                <div className="flex items-center gap-2 mb-2">
                                    <span
                                        className="rounded-full border-2 border-white flex items-center justify-center text-[10px] shrink-0"
                                        style={{
                                            background: getCategory(selected.category).color,
                                            color: "#000",
                                            width: 22,
                                            height: 22,
                                        }}
                                    >
                                        {getCategory(selected.category).icon}
                                    </span>
                                    <span className="label-eyebrow !mb-0">
                                        {t(getCategory(selected.category).label)}
                                    </span>
                                </div>
                                <h3 className="font-display uppercase text-2xl text-white mb-3">
                                    {t(selected.title)}
                                </h3>
                                <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
                                    {selected.description ? t(selected.description) : t("Brak opisu.")}
                                </p>
                                <div className="mt-4 text-xs text-[color:var(--text-muted)] font-mono">
                                    x: {selected.x.toFixed(2)}% · y: {selected.y.toFixed(2)}%
                                </div>
                            </div>
                            {selected.image_url && (
                                <div className="md:col-span-4 md:pt-12">
                                    <button
                                        type="button"
                                        data-testid="map-poi-image-trigger"
                                        onClick={() => setLightboxOpen(true)}
                                        className="group relative block w-full overflow-hidden rounded-md cursor-zoom-in"
                                        aria-label={`Powiększ zdjęcie: ${selected.title}`}
                                    >
                                        <img
                                            src={resolveMediaUrl(selected.image_url)}
                                            alt={selected.title}
                                            loading="lazy"
                                            decoding="async"
                                            className="w-full rounded-md transition-transform duration-500 group-hover:scale-105"
                                        />
                                        <div className="absolute inset-0 bg-[#040914]/0 group-hover:bg-[#040914]/40 transition-colors flex items-center justify-center">
                                            <span className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2 text-white text-xs uppercase tracking-wider font-medium">
                                                <ZoomIn size={16} className="text-[color:var(--primary)]" />
                                                {t("Powiększ")}
                                            </span>
                                        </div>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <AnimatePresence>
                {lightboxOpen && selected?.image_url && (
                    <motion.div
                        data-testid="map-lightbox"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setLightboxOpen(false)}
                        className="fixed inset-0 z-[100] bg-[#040914]/95 backdrop-blur-md flex items-center justify-center p-6 cursor-zoom-out"
                    >
                        <button
                            data-testid="map-lightbox-close"
                            className="absolute top-6 right-6 text-white/80 hover:text-[color:var(--primary)] p-2"
                            onClick={() => setLightboxOpen(false)}
                            aria-label="Zamknij"
                        >
                            <X size={28} />
                        </button>
                        <motion.div
                            initial={{ scale: 0.96, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.25 }}
                            className="max-w-6xl w-full max-h-[85vh]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={resolveMediaUrl(selected.image_url)}
                                alt={selected.title}
                                decoding="async"
                                className="w-full h-full object-contain max-h-[85vh] rounded-md"
                            />
                            <div className="mt-4 text-center">
                                <div className="font-display uppercase tracking-wider text-white">
                                    {selected.title}
                                </div>
                                {selected.description && (
                                    <div className="text-sm text-[color:var(--text-secondary)] mt-1 max-w-2xl mx-auto">
                                        {selected.description}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
